import { useEffect, useRef } from 'react'

/**
 * Holds a Screen Wake Lock (navigator.wakeLock) while `active` is true, keeping
 * the device display awake. The browser automatically releases a wake lock when
 * the tab becomes hidden or the device locks, so we re-acquire it whenever the
 * page returns to visibility while still active.
 *
 * Notes / limitations:
 * - This only keeps the screen on while the tab is visible. It does NOT enable
 *   background work with the screen off / phone locked — that needs a native shell.
 * - The API is unsupported on some browsers (older Safari/Firefox); we degrade
 *   silently rather than throwing.
 */
export function useScreenWakeLock(active: boolean): void {
  // The live WakeLockSentinel, when held. Kept in a ref so visibility/effect
  // handlers can release it without re-running the acquire effect.
  const sentinelRef = useRef<WakeLockSentinel | null>(null)

  useEffect(() => {
    const wakeLock = (navigator as Navigator & { wakeLock?: WakeLock }).wakeLock
    if (!wakeLock) return // Unsupported browser — nothing to do.

    let cancelled = false

    const acquire = async () => {
      // Only hold a lock while active and the tab is actually visible — the OS
      // rejects requests from hidden documents anyway.
      if (!active || document.visibilityState !== 'visible' || sentinelRef.current) return
      try {
        const sentinel = await wakeLock.request('screen')
        if (cancelled || !active) {
          // State changed while awaiting — drop it immediately.
          await sentinel.release().catch(() => {})
          return
        }
        sentinelRef.current = sentinel
        // The browser may release the lock on its own (tab hidden, lock screen);
        // clear our ref so the next visibility change can re-acquire.
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null
        })
      } catch {
        // Permission denied / not allowed in this context — degrade silently.
      }
    }

    const release = () => {
      const sentinel = sentinelRef.current
      sentinelRef.current = null
      if (sentinel) sentinel.release().catch(() => {})
    }

    const handleVisibility = () => {
      if (active && document.visibilityState === 'visible') {
        void acquire()
      }
    }

    if (active) {
      void acquire()
      document.addEventListener('visibilitychange', handleVisibility)
    }

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibility)
      release()
    }
  }, [active])
}
